import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { employeeAPI } from '../../services/api';
import { CompanyTierBadge } from '../../components/common/CompanyTierCard';
import { Building, Calendar, Star, ChevronLeft, Loader2, CheckCircle, Clock, Flag, AlertCircle, ThumbsUp } from 'lucide-react';

const statusConfig = {
  acknowledged: { label: 'Verified', class: 'status-verified', icon: CheckCircle },
  pending_acknowledgment: { label: 'Pending Review', class: 'status-pending', icon: Clock },
  draft: { label: 'Employer Draft', class: 'status-draft', icon: Flag },
  flagged: { label: 'In Discussion', class: 'status-flagged', icon: AlertCircle },
};

const rehireConfig = {
  yes: { label: 'Yes', class: 'bg-green-500/20 text-green-400' },
  conditional: { label: 'Conditional', class: 'bg-amber-500/20 text-amber-400' },
  no: { label: 'No', class: 'bg-red-500/20 text-red-400' },
};

const RecordDetail = () => {
  const { id } = useParams();
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRecord();
  }, [id]);

  const fetchRecord = async () => {
    try {
      const res = await employeeAPI.getRecord(id);
      setRecord(res.data);
    } catch (error) {
      console.error('Error fetching record:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-[#1B5AFF]" />
        </div>
      </DashboardLayout>
    );
  }

  if (!record) {
    return (
      <DashboardLayout>
        <div className="text-center py-12">
          <AlertCircle className="w-12 h-12 text-[#5B6472] mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-[#0A0F1E]">Record not found</h2>
        </div>
      </DashboardLayout>
    );
  }

  const status = statusConfig[record.status] || statusConfig.draft;
  const rehire = rehireConfig[record.rehire_eligibility] || rehireConfig.yes;

  return (
    <DashboardLayout>
      <div className="max-w-3xl space-y-8" data-testid="employee-record-detail">
        {/* Back Link */}
        <Link 
          to="/employee/records" 
          className="inline-flex items-center gap-2 text-[#5B6472] hover:text-[#0A0F1E] transition-colors"
        >
          <ChevronLeft className="w-5 h-5" />
          Back to Records
        </Link>

        {/* Status Banner for Pending */}
        {record.status === 'pending_acknowledgment' && (
          <Card className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Clock className="w-6 h-6 text-amber-400" />
              <div>
                <p className="text-amber-400 font-medium">Pending Review</p>
                <p className="text-amber-400/80 text-sm">Review and acknowledge this record</p>
              </div>
            </div>
            <Link to={`/employee/records/${id}/review`}>
              <Button className="bg-amber-500 hover:bg-amber-600 text-[#0A0F1E] rounded-xl">
                Review Record
              </Button>
            </Link>
          </Card>
        )}

        {/* Header */}
        <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6 animate-fade-in">
          <div className="flex items-start justify-between mb-6">
            <div className="flex items-center gap-4">
              {record.employer_logo ? (
                <img src={record.employer_logo} alt="" className="w-16 h-16 rounded-xl object-cover border border-[#E8EDF5]" />
              ) : (
                <div className="w-16 h-16 rounded-xl bg-[#F5F8FF] border border-[#E8EDF5] flex items-center justify-center">
                  <Building className="w-6 h-6 text-[#5B6472]" />
                </div>
              )}
              <div>
                <h1 className="text-2xl font-bold text-[#0A0F1E]">{record.job_title}</h1>
                <div className="flex items-center gap-3 flex-wrap">
                  <p className="text-[#5B6472]">{record.employer_name || 'Unknown Company'}</p>
                  {record.employer_tier && (
                    <CompanyTierBadge 
                      tier={record.employer_tier.tier} 
                      tier_name={record.employer_tier.tier_name}
                      size="sm"
                    />
                  )}
                </div>
              </div>
            </div>
            <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border ${status.class}`}>
              <status.icon className="w-4 h-4" />
              {status.label}
            </span>
          </div>

          <div className="flex items-center gap-6 text-[#5B6472]">
            <span className="flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              {record.start_date} - {record.currently_employed ? 'Present' : record.end_date}
            </span>
            {record.department && (
              <span className="flex items-center gap-2">
                <Building className="w-5 h-5" />
                {record.department}
              </span>
            )}
          </div>
        </Card>

        {/* Skills */}
        {record.skills?.length > 0 && (
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6 animate-fade-in">
            <h2 className="text-lg font-semibold text-[#0A0F1E] mb-4">Skills Demonstrated</h2>
            <div className="flex flex-wrap gap-2">
              {record.skills.map((skill, i) => (
                <span key={i} className="px-3 py-1.5 rounded-full bg-[#F5F8FF] text-[#0A0F1E] text-sm border border-[#E8EDF5]">
                  {skill}
                </span>
              ))}
            </div>
          </Card>
        )}

        {/* Daily Responsibilities */}
        {record.daily_responsibilities?.length > 0 && (
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6 animate-fade-in">
            <h2 className="text-lg font-semibold text-[#0A0F1E] mb-4">Daily Responsibilities</h2>
            <ul className="list-disc list-inside space-y-2 text-[#0A0F1E]">
              {record.daily_responsibilities.map((resp, i) => (
                <li key={i}>{resp}</li>
              ))}
            </ul>
          </Card>
        )}

        {/* Key Achievements */}
        {record.key_achievements?.length > 0 && (
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6 animate-fade-in">
            <h2 className="text-lg font-semibold text-[#0A0F1E] mb-4">Key Achievements</h2>
            <ul className="list-disc list-inside space-y-2 text-[#0A0F1E]">
              {record.key_achievements.map((ach, i) => (
                <li key={i}>{ach}</li>
              ))}
            </ul>
          </Card>
        )}

        {/* Strengths & Growth */}
        <div className="grid md:grid-cols-2 gap-6">
          {record.strengths && (
            <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6 animate-fade-in">
              <h2 className="text-lg font-semibold text-[#0A0F1E] mb-4 flex items-center gap-2">
                <ThumbsUp className="w-5 h-5 text-green-400" />
                Top Strengths
              </h2>
              <p className="text-[#0A0F1E] whitespace-pre-wrap">{record.strengths}</p>
            </Card>
          )}
          {record.growth_areas && (
            <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6 animate-fade-in">
              <h2 className="text-lg font-semibold text-[#0A0F1E] mb-4 flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-amber-400" />
                Areas of Growth
              </h2>
              <p className="text-[#0A0F1E] whitespace-pre-wrap">{record.growth_areas}</p>
            </Card>
          )}
        </div>

        {/* Rehire & Rating */}
        <div className="grid md:grid-cols-2 gap-6">
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6 animate-fade-in">
            <h2 className="text-lg font-semibold text-[#0A0F1E] mb-4">Rehire Eligibility</h2>
            <span className={`inline-flex items-center px-4 py-2 rounded-xl text-sm font-medium ${rehire.class}`}>
              {rehire.label}
            </span>
            {record.rehire_note && (
              <p className="text-[#5B6472] mt-3 text-sm">{record.rehire_note}</p>
            )}
            {record.rehire_condition && (
              <p className="text-[#5B6472] mt-3 text-sm">{record.rehire_condition}</p>
            )}
          </Card>
          {record.rating && (
            <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6 animate-fade-in">
              <h2 className="text-lg font-semibold text-[#0A0F1E] mb-4">Overall Rating</h2>
              <div className="flex items-center gap-1">
                {[...Array(5)].map((_, i) => (
                  <Star 
                    key={i} 
                    className={`w-8 h-8 ${i < record.rating ? 'fill-[#1B5AFF] text-[#1B5AFF]' : 'text-[#5B6472]'}`} 
                  />
                ))}
              </div>
            </Card>
          )}
        </div>

        {/* Performance Ratings */}
        {(record.punctuality_rating || record.deadline_rating || record.initiative_rating || record.adaptability_rating) && (
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6 animate-fade-in">
            <h2 className="text-lg font-semibold text-[#0A0F1E] mb-4">Performance Ratings</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {record.punctuality_rating && (
                <div className="text-center p-3 rounded-lg bg-[#F5F8FF]">
                  <p className="text-[#5B6472] text-xs mb-1">Punctuality</p>
                  <p className="text-2xl font-bold text-[#1B5AFF]">{record.punctuality_rating}/5</p>
                </div>
              )}
              {record.deadline_rating && (
                <div className="text-center p-3 rounded-lg bg-[#F5F8FF]">
                  <p className="text-[#5B6472] text-xs mb-1">Deadlines</p>
                  <p className="text-2xl font-bold text-[#1B5AFF]">{record.deadline_rating}/5</p>
                </div>
              )}
              {record.initiative_rating && (
                <div className="text-center p-3 rounded-lg bg-[#F5F8FF]">
                  <p className="text-[#5B6472] text-xs mb-1">Initiative</p>
                  <p className="text-2xl font-bold text-[#1B5AFF]">{record.initiative_rating}/5</p>
                </div>
              )}
              {record.adaptability_rating && (
                <div className="text-center p-3 rounded-lg bg-[#F5F8FF]">
                  <p className="text-[#5B6472] text-xs mb-1">Adaptability</p>
                  <p className="text-2xl font-bold text-[#1B5AFF]">{record.adaptability_rating}/5</p>
                </div>
              )}
            </div>
            {record.work_ethic_note && (
              <p className="text-[#0A0F1E] mt-4 text-sm">{record.work_ethic_note}</p>
            )}
          </Card>
        )}

        {/* Collaboration Style & Team Perception */}
        {(record.collaboration_style || record.peer_team_perception) && (
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6 animate-fade-in">
            <h2 className="text-lg font-semibold text-[#0A0F1E] mb-4">Collaboration & Team</h2>
            {record.collaboration_style && (
              <div className="mb-4">
                <p className="text-[#5B6472] text-sm mb-1">Collaboration Style</p>
                <p className="text-[#0A0F1E] capitalize">{record.collaboration_style.replace(/_/g, ' ')}</p>
                {record.collaboration_note && (
                  <p className="text-[#5B6472] text-sm mt-2">{record.collaboration_note}</p>
                )}
              </div>
            )}
            {record.peer_team_perception && (
              <div>
                <p className="text-[#5B6472] text-sm mb-1">Team Perception</p>
                <p className="text-[#0A0F1E]">{record.peer_team_perception}</p>
              </div>
            )}
          </Card>
        )}

        {/* Departure Details */}
        {(record.reason_for_departure || record.departure_note) && (
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6 animate-fade-in">
            <h2 className="text-lg font-semibold text-[#0A0F1E] mb-4">Departure Details</h2>
            {record.reason_for_departure && (
              <div className="mb-3">
                <p className="text-[#5B6472] text-sm mb-1">Reason for Departure</p>
                <p className="text-[#0A0F1E] capitalize">{record.reason_for_departure.replace(/_/g, ' ')}</p>
              </div>
            )}
            {record.departure_note && (
              <p className="text-[#0A0F1E]">{record.departure_note}</p>
            )}
          </Card>
        )}

        {/* Professional Closing Statement */}
        {record.closing_statement && (
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6 animate-fade-in">
            <h2 className="text-lg font-semibold text-[#0A0F1E] mb-4">Professional Closing Statement</h2>
            <p className="text-[#0A0F1E] whitespace-pre-wrap leading-relaxed">{record.closing_statement}</p>
          </Card>
        )}

        {/* Public Note */}
        {record.public_note && (
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6 animate-fade-in">
            <h2 className="text-lg font-semibold text-[#0A0F1E] mb-4">Public Note</h2>
            <p className="text-[#0A0F1E]">{record.public_note}</p>
          </Card>
        )}

        {/* Flags */}
        {record.flags?.length > 0 && (
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6 animate-fade-in">
            <h2 className="text-lg font-semibold text-[#0A0F1E] mb-4 flex items-center gap-2">
              <Flag className="w-5 h-5 text-[#1B5AFF]" />
              Flags
            </h2>
            <div className="space-y-4">
              {record.flags.map((flag) => (
                <Link 
                  key={flag.id} 
                  to={`/employee/flags/${record.id}/${flag.id}`}
                  className="block p-4 rounded-xl bg-[#F5F8FF]/50 border border-[#E8EDF5] hover:border-[#1B5AFF]/50 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-[#0A0F1E] font-medium capitalize">{flag.field_name.replace(/_/g, ' ')}</p>
                      <p className="text-[#5B6472] text-sm mt-1">{flag.employee_message}</p>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      flag.status === 'resolved' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                    }`}>
                      {flag.status}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};

export default RecordDetail;
